import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Search, Package, Copy,
  Truck, Clock, CheckCircle, Box, Send,
  User, Mail, Phone, MapPin, Hash, Calendar, FileText, ExternalLink
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  received:    { label: "Recibido",   color: "bg-stone-100 text-stone-700", icon: Box },
  in_progress: { label: "En Proceso", color: "bg-amber-100 text-amber-700", icon: Clock },
  shipped:     { label: "Enviado",    color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  delivered:   { label: "Entregado",  color: "bg-blue-100 text-blue-700", icon: Truck }
};

const OrdersPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    client_id: "", client_name: "", client_email: "", description: "",
    items: [{ description: "", qty: 1 }], notes: "", revenue: "",
    cost_materials: "", cost_production: "", cost_logistics: "", cost_financial: "", cost_advertising: ""
  });

  // Status update dialog
  const [statusOrder, setStatusOrder] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");

  // Order details dialog
  const [detailsOrder, setDetailsOrder] = useState(null);
  const [carrierTracking, setCarrierTracking] = useState("");

  useEffect(() => {
    if (!user || user.role !== "admin") { navigate("/"); return; }
    fetchData();
    // fetchData is a stable function defined in component scope and depends on token only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const fetchData = async () => {
    try {
      const [ordRes, cliRes, prodRes] = await Promise.all([
        axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/clients`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setOrders(ordRes.data.orders);
      setClients(cliRes.data.clients);
      setProducts(prodRes.data.products.filter(p => p.is_active !== false));
    } catch (e) { toast.error("Error al cargar datos"); }
    finally { setIsLoading(false); }
  };

  const handleClientSelect = (clientId) => {
    const c = clients.find(cl => cl.id === clientId);
    if (c) setForm(p => ({ ...p, client_id: clientId, client_name: c.name, client_email: c.email }));
  };

  const updateItem = (idx, field, value) => {
    setForm(p => {
      const items = [...p.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...p, items };
    });
  };

  const handleCreate = async () => {
    if (!form.client_name) { toast.error("Selecciona un cliente"); return; }
    if (!form.items.some(i => i.description)) { toast.error("Agrega al menos un item"); return; }
    setIsSaving(true);
    try {
      const payload = {
        ...form,
        revenue: parseFloat(form.revenue) || 0,
        cost_materials: parseFloat(form.cost_materials) || 0,
        cost_production: parseFloat(form.cost_production) || 0,
        cost_logistics: parseFloat(form.cost_logistics) || 0,
        cost_financial: parseFloat(form.cost_financial) || 0,
        cost_advertising: parseFloat(form.cost_advertising) || 0,
      };
      await axios.post(`${API}/admin/orders`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Pedido creado y correo enviado al cliente");
      setIsFormOpen(false);
      fetchData();
    } catch (e) { toast.error("Error al crear pedido"); }
    finally { setIsSaving(false); }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus || !statusOrder) return;
    try {
      await axios.put(`${API}/admin/orders/${statusOrder.id}/status`, {
        status: newStatus, note: statusNote || undefined,
        carrier_tracking: carrierTracking || undefined
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Estado actualizado y cliente notificado");
      setStatusOrder(null);
      setNewStatus("");
      setStatusNote("");
      setCarrierTracking("");
      fetchData();
    } catch (e) { toast.error("Error al actualizar estado"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este pedido?")) return;
    try {
      await axios.delete(`${API}/admin/orders/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Pedido eliminado");
      fetchData();
    } catch (e) { toast.error("Error al eliminar"); }
  };

  const copyTracking = (num) => {
    navigator.clipboard.writeText(`${window.location.origin}/tracking/${num}`);
    toast.success("Enlace de seguimiento copiado");
  };

  const filtered = orders.filter(o => {
    const matchSearch = o.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.tracking_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === "all" || o.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const getNextStatus = (current) => {
    const idx = ["received", "in_progress", "shipped", "delivered"].indexOf(current);
    return idx < 3 ? ["received", "in_progress", "shipped", "delivered"][idx + 1] : null;
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="orders-page">
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="orders-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Admin
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <Package className="w-5 h-5 text-indigo-600" /> Pedidos & Seguimiento
              </h1>
              <p className="text-xs text-stone-500">{orders.length} pedidos</p>
            </div>
          </div>
          <Button onClick={() => { setForm({ client_id: "", client_name: "", client_email: "", description: "", items: [{ description: "", qty: 1 }], notes: "", revenue: "", cost_materials: "", cost_production: "", cost_logistics: "", cost_financial: "", cost_advertising: "" }); setIsFormOpen(true); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-order-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Pedido
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input placeholder="Buscar por cliente o tracking..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-white" />
          </div>
          <div className="flex gap-1">
            {["all", "received", "in_progress", "shipped", "delivered"].map(s => (
              <Button key={s} size="sm" variant={filterStatus === s ? "default" : "outline"}
                onClick={() => setFilterStatus(s)}
                className={filterStatus === s ? "bg-stone-900" : ""}>
                {s === "all" ? "Todos" : STATUS_CONFIG[s]?.label}
              </Button>
            ))}
          </div>
        </div>

        {isLoading ? <div className="text-center py-12 text-stone-500">Cargando...</div> :
        filtered.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <p className="text-stone-500 text-lg">No hay pedidos</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(order => {
              const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.received;
              const StatusIcon = cfg.icon;
              const next = getNextStatus(order.status);
              return (
                <Card key={order.id} className="hover:shadow-md hover:border-indigo-200 transition-all cursor-pointer" data-testid={`order-card-${order.id}`} onClick={() => setDetailsOrder(order)}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                          <StatusIcon className="w-6 h-6 text-indigo-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono font-bold text-indigo-600 cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); copyTracking(order.tracking_number); }} data-testid={`order-tracking-${order.tracking_number}`}>
                              {order.tracking_number}
                            </span>
                            <Copy className="w-3 h-3 text-stone-400 cursor-pointer" onClick={(e) => { e.stopPropagation(); copyTracking(order.tracking_number); }} />
                          </div>
                          <p className="font-medium text-stone-800">{order.client_name}</p>
                          <p className="text-xs text-stone-500">{(order.items || []).map(i => i.description).filter(Boolean).join(", ") || order.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                        <Badge className={cfg.color}>
                          <StatusIcon className="w-3 h-3 mr-1" /> {cfg.label}
                        </Badge>
                        <span className="text-xs text-stone-400">{new Date(order.created_at).toLocaleDateString()}</span>
                        <div className="flex gap-1">
                          {next && (
                            <Button size="sm" variant="outline" className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                              onClick={() => { setStatusOrder(order); setNewStatus(next); setStatusNote(""); }}
                              data-testid={`order-advance-${order.id}`}>
                              <Send className="w-3 h-3 mr-1" /> {STATUS_CONFIG[next]?.label}
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDelete(order.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Order Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5" /> Nuevo Pedido</DialogTitle>
            <DialogDescription>El cliente recibirá un correo con el número de seguimiento.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <Select value={form.client_id} onValueChange={handleClientSelect}>
                <SelectTrigger data-testid="order-client-select"><SelectValue placeholder="Seleccionar cliente..." /></SelectTrigger>
                <SelectContent>
                  {clients.filter(c => c.is_approved !== false).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Items del Pedido</Label>
              <div className="space-y-2">
                {products.length > 0 && (
                  <Select onValueChange={(productId) => {
                    const p = products.find(pr => pr.id === productId);
                    if (p) setForm(prev => ({ ...prev, items: [...prev.items.filter(i => i.description), { description: p.name, qty: 1 }] }));
                  }}>
                    <SelectTrigger className="border-dashed border-indigo-300 text-indigo-600" data-testid="order-product-select">
                      <SelectValue placeholder="Seleccionar del catálogo..." />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} {p.price ? `— $${p.price.toFixed(2)}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {form.items.map((item, idx) => (
                  <div key={item._uid || idx} className="flex gap-2">
                    <Input className="flex-1" placeholder="Descripción (ej: 1000 Business Cards)" value={item.description} onChange={(e) => updateItem(idx, "description", e.target.value)} data-testid={`order-item-${idx}`} />
                    <Input className="w-20" type="number" placeholder="Cant." value={item.qty} onChange={(e) => updateItem(idx, "qty", e.target.value)} min="1" />
                    {form.items.length > 1 && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }))}><Trash2 className="w-4 h-4" /></Button>}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setForm(p => ({ ...p, items: [...p.items, { _uid: `row-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, description: "", qty: 1 }] }))} data-testid="order-add-item">
                  <Plus className="w-4 h-4 mr-1" /> Agregar Item Manual
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notas internas</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Notas sobre el pedido..." rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2 border-t">
              <div className="space-y-2 col-span-2">
                <Label className="text-blue-700">💰 Ingreso ($)</Label>
                <Input type="number" step="0.01" value={form.revenue}
                  onChange={(e) => setForm(p => ({ ...p, revenue: e.target.value }))}
                  placeholder="0.00" data-testid="order-revenue" />
              </div>
              <p className="col-span-2 text-xs font-semibold text-stone-600 uppercase mt-2">Desglose de Gastos</p>
              <div className="space-y-2">
                <Label className="text-xs text-amber-700">📦 Materiales</Label>
                <Input type="number" step="0.01" value={form.cost_materials}
                  onChange={(e) => setForm(p => ({ ...p, cost_materials: e.target.value }))}
                  placeholder="0.00" data-testid="order-cost-materials" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-orange-700">⚙️ Producción</Label>
                <Input type="number" step="0.01" value={form.cost_production}
                  onChange={(e) => setForm(p => ({ ...p, cost_production: e.target.value }))}
                  placeholder="0.00" data-testid="order-cost-production" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-red-700">🚚 Logística</Label>
                <Input type="number" step="0.01" value={form.cost_logistics}
                  onChange={(e) => setForm(p => ({ ...p, cost_logistics: e.target.value }))}
                  placeholder="0.00" data-testid="order-cost-logistics" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-rose-700">💳 Fees</Label>
                <Input type="number" step="0.01" value={form.cost_financial}
                  onChange={(e) => setForm(p => ({ ...p, cost_financial: e.target.value }))}
                  placeholder="0.00" data-testid="order-cost-financial" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-xs text-pink-700">📢 Publicidad</Label>
                <Input type="number" step="0.01" value={form.cost_advertising}
                  onChange={(e) => setForm(p => ({ ...p, cost_advertising: e.target.value }))}
                  placeholder="0.00" data-testid="order-cost-advertising" />
              </div>
              <p className="col-span-2 text-xs text-stone-500">Para el reporte financiero. Se pueden editar después.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFormOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={isSaving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="order-save-btn">
              {isSaving ? "Creando..." : "Crear Pedido"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Details Dialog */}
      <Dialog open={!!detailsOrder} onOpenChange={(o) => !o && setDetailsOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="order-details-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-indigo-600" />
              Detalles del Pedido
            </DialogTitle>
            <DialogDescription>
              <span className="font-mono font-semibold text-indigo-600">{detailsOrder?.tracking_number}</span>
            </DialogDescription>
          </DialogHeader>
          {detailsOrder && (() => {
            const client = clients.find(c => c.id === detailsOrder.client_id) || {};
            const cfg2 = STATUS_CONFIG[detailsOrder.status] || STATUS_CONFIG.received;
            const StatusIcon = cfg2.icon;
            const fullAddress = [
              client.street_address,
              client.unit ? `Apt/Unit ${client.unit}` : "",
              [client.city, client.state, client.zip_code].filter(Boolean).join(", "),
            ].filter(Boolean).join(" · ");
            return (
              <div className="space-y-4 py-2">
                {/* Status row */}
                <div className="flex items-center justify-between rounded-xl bg-stone-50 p-3 border">
                  <Badge className={cfg2.color}>
                    <StatusIcon className="w-3 h-3 mr-1" /> {cfg2.label}
                  </Badge>
                  <span className="text-xs text-stone-500">
                    <Calendar className="w-3 h-3 inline mr-1" />
                    Creado: {new Date(detailsOrder.created_at).toLocaleString()}
                  </span>
                </div>

                {/* Client info */}
                <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4 space-y-3" data-testid="order-details-client">
                  <h3 className="text-sm font-semibold text-indigo-900 flex items-center gap-2">
                    <User className="w-4 h-4" /> Información del Cliente
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-stone-500 flex items-center gap-1"><User className="w-3 h-3" /> Nombre</p>
                      <p className="font-medium text-stone-800">{detailsOrder.client_name || client.name || "—"}</p>
                    </div>
                    {client.code && (
                      <div>
                        <p className="text-xs text-stone-500 flex items-center gap-1"><Hash className="w-3 h-3" /> Código de cliente</p>
                        <p className="font-mono font-semibold text-indigo-700">{client.code}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-stone-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</p>
                      {(detailsOrder.client_email || client.email) ? (
                        <a href={`mailto:${detailsOrder.client_email || client.email}`} className="text-indigo-600 hover:underline break-all">
                          {detailsOrder.client_email || client.email}
                        </a>
                      ) : <p className="text-stone-400">—</p>}
                    </div>
                    <div>
                      <p className="text-xs text-stone-500 flex items-center gap-1"><Phone className="w-3 h-3" /> Teléfono</p>
                      {client.phone ? (
                        <a href={`tel:${client.phone}`} className="text-indigo-600 hover:underline">{client.phone}</a>
                      ) : <p className="text-stone-400">—</p>}
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-xs text-stone-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Dirección</p>
                      {fullAddress ? (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`}
                          target="_blank" rel="noreferrer"
                          className="text-indigo-600 hover:underline inline-flex items-center gap-1">
                          {fullAddress} <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : <p className="text-stone-400">—</p>}
                    </div>
                    {client.service_types && client.service_types.length > 0 && (
                      <div className="md:col-span-2">
                        <p className="text-xs text-stone-500">Servicios contratados</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {client.service_types.map(s => (
                            <Badge key={s} variant="outline" className="text-xs bg-white">{s}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {client.id && (
                    <Button size="sm" variant="ghost" className="text-indigo-600 hover:bg-indigo-100 -ml-3"
                      onClick={() => { setDetailsOrder(null); navigate(`/admin/clients`); }}
                      data-testid="order-details-open-client">
                      Ver perfil completo del cliente <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  )}
                </div>

                {/* Items */}
                <div className="rounded-xl border p-4">
                  <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2 mb-3">
                    <Box className="w-4 h-4" /> Items del Pedido
                  </h3>
                  {(detailsOrder.items || []).length === 0 ? (
                    <p className="text-sm text-stone-400">Sin items registrados</p>
                  ) : (
                    <ul className="divide-y">
                      {detailsOrder.items.map((it, i) => (
                        <li key={i} className="py-2 flex items-center justify-between text-sm">
                          <span className="text-stone-800">{it.description || "—"}</span>
                          <span className="text-stone-500">x {it.qty || 1}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {detailsOrder.description && !detailsOrder.items?.length && (
                    <p className="text-sm text-stone-700 mt-2">{detailsOrder.description}</p>
                  )}
                </div>

                {/* Carrier tracking */}
                {detailsOrder.carrier_tracking && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
                    <p className="text-xs text-stone-500">Tracking del courier</p>
                    <p className="font-mono font-semibold text-emerald-700">{detailsOrder.carrier_tracking}</p>
                  </div>
                )}

                {/* Notes */}
                {detailsOrder.notes && (
                  <div className="rounded-xl border p-4">
                    <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4" /> Notas internas
                    </h3>
                    <p className="text-sm text-stone-600 whitespace-pre-wrap">{detailsOrder.notes}</p>
                  </div>
                )}

                {/* Status history */}
                {detailsOrder.status_history && detailsOrder.status_history.length > 0 && (
                  <div className="rounded-xl border p-4">
                    <h3 className="text-sm font-semibold text-stone-800 mb-3">Historial de Estados</h3>
                    <ul className="space-y-2 text-sm">
                      {detailsOrder.status_history.map((h, i) => {
                        const c = STATUS_CONFIG[h.status] || {};
                        return (
                          <li key={i} className="flex items-start gap-3">
                            <Badge className={c.color || "bg-stone-100 text-stone-700"}>{c.label || h.status}</Badge>
                            <div className="flex-1">
                              <p className="text-xs text-stone-500">{new Date(h.date).toLocaleString()}</p>
                              {h.note && <p className="text-stone-700">{h.note}</p>}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsOrder(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={!!statusOrder} onOpenChange={(o) => !o && setStatusOrder(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Actualizar Estado</DialogTitle>
            <DialogDescription>
              {statusOrder?.tracking_number} - {statusOrder?.client_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nuevo Estado</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["received", "in_progress", "shipped", "delivered"].map(s => (
                    <SelectItem key={s} value={s}>{STATUS_CONFIG[s].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {newStatus === "shipped" && (
              <div className="space-y-2">
                <Label>Número de Seguimiento del Courier</Label>
                <Input value={carrierTracking} onChange={(e) => setCarrierTracking(e.target.value.trim())} placeholder="FedEx, UPS o USPS tracking number" className="font-mono" data-testid="carrier-tracking-input" />
                <p className="text-xs text-stone-500">Se enviará al cliente en el correo con enlace directo al rastreo</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Nota (opcional)</Label>
              <Input value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="Ej: Impresión completada" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusOrder(null)}>Cancelar</Button>
            <Button onClick={handleUpdateStatus} className="bg-indigo-600 hover:bg-indigo-700" data-testid="order-status-btn">Actualizar y Notificar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersPage;
